import {
  afterNextRender,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
} from '@angular/core';
import { GoogleMapsLoaderService } from '../../services/application/google-maps-loader.service';

export interface PlacePickedEvent {
  address: string;
  lat: number;
  lng: number;
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

interface PlaceAddressComponent {
  longText?: string;
  shortText?: string;
  types?: string[];
}

@Directive({
  selector: '[appPlacesAutocomplete]',
  standalone: true,
})
export class PlacesAutocompleteDirective implements OnDestroy {
  private _el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  @Output() placePicked = new EventEmitter<PlacePickedEvent>();

  private _dropdown: HTMLElement | null = null;
  private _debounce: ReturnType<typeof setTimeout> | null = null;
  private readonly _mapsLoader = inject(GoogleMapsLoaderService);

  constructor() {
    afterNextRender(() => this._mapsLoader.load().then(() => this._init()));
  }

  public ngOnDestroy(): void {
    this._dropdown?.remove();
    if (this._debounce) clearTimeout(this._debounce);
  }

  private _init(): void {
    const input = this._el.nativeElement;

    this._dropdown = document.createElement('div');
    this._dropdown.className = 'places-dropdown';

    const parent = input.parentElement!;
    parent.style.position = 'relative';
    parent.appendChild(this._dropdown);

    input.addEventListener('input', () => {
      if (this._debounce) clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this._fetchSuggestions(), 300);
    });

    input.addEventListener('blur', () => {
      setTimeout(() => this._closeDropdown(), 200);
    });
  }

  private async _fetchSuggestions(): Promise<void> {
    const input = this._el.nativeElement;
    const value = input.value.trim();

    if (!value || value.length < 2) {
      this._closeDropdown();
      return;
    }

    const g = (window as any).google;
    if (!g?.maps) return;

    try {
      const { AutocompleteSuggestion } = await (g.maps.importLibrary(
        'places',
      ) as Promise<any>);
      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
        });

      if (!suggestions?.length) {
        this._closeDropdown();
        return;
      }

      this._renderSuggestions(suggestions);
    } catch {
      this._closeDropdown();
    }
  }

  private _renderSuggestions(suggestions: any[]): void {
    if (!this._dropdown) return;
    this._dropdown.innerHTML = '';
    this._dropdown.style.display = 'block';

    for (const s of suggestions) {
      const item = document.createElement('div');
      item.className = 'places-dropdown__item';
      item.textContent = s.placePrediction.text.toString();

      item.addEventListener('mousedown', async (e) => {
        e.preventDefault();
        await this._selectSuggestion(s);
      });

      this._dropdown.appendChild(item);
    }
  }

  private async _selectSuggestion(suggestion: any): Promise<void> {
    const input = this._el.nativeElement;

    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({
      fields: ['formattedAddress', 'location', 'addressComponents'],
    });

    const lat = place.location?.lat();
    const lng = place.location?.lng();
    const address = place.formattedAddress || input.value;
    const components = place.addressComponents ?? [];
    const component = (...types: string[]): string | undefined => {
      const match = components.find((item: PlaceAddressComponent) =>
        types.some((type) => item.types?.includes(type)),
      );
      return match?.longText ?? match?.shortText;
    };
    const streetAddress = [component('street_number'), component('route')]
      .filter(Boolean)
      .join(' ');

    input.value = address;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this._closeDropdown();

    if (lat != null && lng != null) {
      this.placePicked.emit({
        address,
        lat,
        lng,
        streetAddress,
        city:
          component('locality') ??
          component('postal_town') ??
          component('administrative_area_level_2'),
        state: component('administrative_area_level_1'),
        zip: component('postal_code'),
        country: component('country'),
      });
    }
  }

  private _closeDropdown(): void {
    if (this._dropdown) this._dropdown.style.display = 'none';
  }
}
